import { useEffect, useState } from "react";
import axios from "axios";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import { baseUrl } from "../../Constant";

const diamondIcon = new Image();
diamondIcon.src = `${process.env.PUBLIC_URL}/images/Oracle/awards/trophydiamond.png`;
diamondIcon.onload = () => {
  ChartJS.defaults.animation = false;
};

const diamondPlugin = {
  id: "diamondPlugin",
  afterDraw(chart) {
    const { ctx } = chart;
    const dataset = chart.getDatasetMeta(0);

    if (!diamondIcon.complete || diamondIcon.naturalWidth === 0) return;

    dataset.data.forEach((bar, index) => {
      const label = chart.data.labels[index];

      if (label === "Americas Market") {
        const size = 32;
        const gap = 6;
        const x = bar.x - size / 2;
        const y = bar.y - size - gap;

        ctx.save();
        ctx.drawImage(diamondIcon, x, y, size, size);
        ctx.restore();
      }
    });
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  diamondPlugin
);

const DiamondCertificationsChart = ({ isAdmin }) => {
  const [data, setData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchData = async () => {
    try {
      const res = await axios.get(`${baseUrl}/fetch`);
      setData(res.data || []);
    } catch (err) {
      console.error("Fetch failed", err);
      setData([]);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpload = async () => {
    if (!uploadFile) {
      setSnackbar({
        open: true,
        message: "Please select an Excel file",
        severity: "warning",
      });
      return;
    }

    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      await axios.post(`${baseUrl}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnackbar({
        open: true,
        message: "Data uploaded successfully",
        severity: "success",
      });

      setShowModal(false);
      setUploadFile(null);
      fetchData();
    } catch (err) {
      console.error("Upload failed", err);
      setSnackbar({
        open: true,
        message: "Upload failed. Please try again.",
        severity: "error",
      });
    }
  };

  const chartData = {
    labels: data.map((d) => d.market),
    datasets: [
      {
        data: data.map((d) => d.value),
        backgroundColor: "#1e88e5",
        borderRadius: 4,
        barThickness: 40,
        maxBarThickness: 50,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 40,
      },
    },
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        Certifications by Market
      </h2>

      {isAdmin && (
        <div style={{ textAlign: "right", marginBottom: "20px" }}>
          <button
            className="oracle-upload-btn"
            onClick={() => setShowModal(true)}
          >
            Upload File
          </button>
        </div>
      )}

      {data.length === 0 ? (
        <p style={{ textAlign: "center" }}>No data available.</p>
      ) : (
        <div style={{ height: "300px", maxWidth: "100%" }}>
          <Bar
            data={chartData}
            options={chartOptions}
            plugins={[diamondPlugin]}
          />
        </div>
      )}

      {showModal && (
        <div className="oracle-modal-overlay">
          <div className="oracle-modal">
            <h3>Upload Diamond Excel</h3>

            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => setUploadFile(e.target.files[0])}
            />

            <div className="oracle-modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowModal(false);
                  setUploadFile(null);
                }}
              >
                Cancel
              </button>

              <button className="btn-upload" onClick={handleUpload}>
                Upload
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default DiamondCertificationsChart;
